/**
 * Shared sorting helpers for the enquiry queue tables. Used by both the user's
 * own enquiries page and the staff "Agent enquiries" page so column ordering
 * behaves identically across the two.
 */

export const readStoredSort = (key) => {
	try {
		const raw = window.localStorage.getItem(key)
		if (!raw) return { key: "", direction: "" }
		const parsed = JSON.parse(raw)
		if (parsed && typeof parsed.key === "string" && typeof parsed.direction === "string") {
			return parsed
		}
	} catch (err) {
		// ignore parse/storage errors and fall back to default
	}
	return { key: "", direction: "" }
}

export const writeStoredSort = (key, value) => {
	try {
		window.localStorage.setItem(key, JSON.stringify(value))
	} catch (err) {
		// ignore storage errors (quota, private mode, etc.)
	}
}

export const sortByCreatedDesc = (a, b) => {
	const aCreated = a.created?._seconds || a.created?.seconds || 0
	const bCreated = b.created?._seconds || b.created?.seconds || 0
	return bCreated - aCreated
}

export const getTimestampSeconds = (timestamp) => timestamp?._seconds || timestamp?.seconds || 0

export const compareValues = (a, b) => {
	if (typeof a === "number" || typeof b === "number") {
		return (a || 0) - (b || 0)
	}
	return String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: "base" })
}

export const getQueueSortValue = (enquiry, key, clientsById) => {
	const client = clientsById[enquiry.client] || {}
	switch (key) {
		case "site":
			return enquiry.site
		case "name":
			return `${client.first_name || ""} ${client.last_name || ""}`.trim()
		case "email":
			return client.email
		case "telephone":
			return client.phone
		case "follow_up":
			return getTimestampSeconds(enquiry.follow_up)
		case "location":
			return enquiry.location
		case "date_from":
			return getTimestampSeconds(enquiry.date_from)
		case "group_size":
			return enquiry.group_size
		case "quoted":
			return enquiry.quoted
		case "age":
			return Math.floor(Date.now() / 1000) - getTimestampSeconds(enquiry.created)
		default:
			return ""
	}
}

export const sortItems = (items, sort, getValue, defaultSort = sortByCreatedDesc) => {
	if (!sort?.key || !sort?.direction) return [...items].sort(defaultSort)

	return [...items].sort((a, b) => {
		const comparison = compareValues(getValue(a, sort.key), getValue(b, sort.key))
		return sort.direction === "asc" ? comparison : -comparison
	})
}

export const nextSortState = (currentSort, key) => {
	if (currentSort.key !== key) return { key, direction: "asc" }
	if (currentSort.direction === "asc") return { key, direction: "desc" }
	return { key: "", direction: "" }
}
